/**
 * TimelineAudioEngine
 *
 * Web Audio API engine for precise, gapless audio playback on a timeline.
 * Pre-fetches and decodes audio files into AudioBuffers so playback starts
 * instantly with zero loading delay — eliminating the lag that HTML5 Audio
 * elements introduce when fetching on first play.
 *
 * Usage:
 *   const engine = new TimelineAudioEngine()
 *   await engine.preload([{ id: 'clip1', url: '/audio/clip1.mp3' }])
 *   engine.play('clip1', 500)   // play clip1 starting at 500ms offset
 *   engine.syncPlayback('clip2', 0)  // during continuous playback, switch only if clip changed
 *   engine.stop()
 *   engine.destroy()
 */
export default class TimelineAudioEngine {
    constructor() {
        /** @type {AudioContext|null} */
        this.audioContext = null
        /** @type {Map<string, AudioBuffer>} clipId -> decoded AudioBuffer */
        this.buffers = new Map()
        /** @type {Map<string, string>} clipId -> URL the buffer was loaded from */
        this.bufferUrls = new Map()
        /** @type {Map<string, Promise>} clipId -> in-flight load promise */
        this.loading = new Map()
        /** @type {AudioBufferSourceNode|null} */
        this.currentSource = null
        /** @type {string|null} */
        this.currentClipId = null
        /** @type {GainNode|null} */
        this.gainNode = null
    }

    // ============================
    // Context management
    // ============================

    /**
     * Lazily create the AudioContext and master gain node.
     * Safe to call multiple times — only creates once.
     */
    _ensureContext() {
        if (!this.audioContext) {
            const Ctx = window.AudioContext || window.webkitAudioContext
            this.audioContext = new Ctx()
            this.gainNode = this.audioContext.createGain()
            this.gainNode.connect(this.audioContext.destination)
        }
        return this.audioContext
    }

    /**
     * Resume the AudioContext if it was suspended.
     * Call this inside a user-gesture handler (e.g. play button click)
     * to satisfy browser autoplay policies.
     */
    async resume() {
        this._ensureContext()
        if (this.audioContext.state === 'suspended') {
            await this.audioContext.resume()
        }
    }

    // ============================
    // Preloading
    // ============================

    /**
     * Preload a list of audio clips. Each clip is fetched, decoded into an
     * AudioBuffer, and cached. Already-loaded or in-flight clips are skipped.
     *
     * @param {Array<{id: string, url: string}>} clips
     * @returns {Promise<void>} resolves when all clips finish loading (or fail gracefully)
     */
    async preload(clips) {
        const ctx = this._ensureContext()

        const tasks = clips
            .filter(clip => clip.id && clip.url)
            .filter(clip => {
                // Skip if already loaded with the same URL and not currently loading
                if (this.loading.has(clip.id)) return false
                if (this.buffers.has(clip.id) && this.bufferUrls.get(clip.id) === clip.url) return false
                // URL changed (e.g. script recalled from history) — evict stale buffer
                if (this.buffers.has(clip.id)) {
                    this.buffers.delete(clip.id)
                    this.bufferUrls.delete(clip.id)
                }
                return true
            })
            .map(clip => {
                const promise = this._loadClip(ctx, clip.id, clip.url)
                this.loading.set(clip.id, promise)
                return promise
            })

        await Promise.allSettled(tasks)
    }

    /**
     * @private
     */
    async _loadClip(ctx, id, url) {
        try {
            const response = await fetch(url)
            if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`)
            const arrayBuffer = await response.arrayBuffer()
            const audioBuffer = await ctx.decodeAudioData(arrayBuffer)
            this.buffers.set(id, audioBuffer)
            this.bufferUrls.set(id, url)
        } catch (err) {
            console.warn(`[TimelineAudioEngine] Failed to preload clip "${id}":`, err)
        } finally {
            this.loading.delete(id)
        }
    }

    /**
     * Check whether a clip has been fully decoded and is ready for instant playback.
     * @param {string} id
     * @returns {boolean}
     */
    isLoaded(id) {
        return this.buffers.has(id)
    }

    /**
     * Get the actual decoded duration of a clip in milliseconds.
     * Returns null if the clip hasn't been loaded yet.
     * This is the most accurate duration — it comes from the decoded AudioBuffer
     * rather than metadata, so it reflects exactly what the Web Audio API will play.
     *
     * @param {string} id
     * @returns {number|null} duration in milliseconds, or null if not loaded
     */
    getBufferDurationMs(id) {
        const buffer = this.buffers.get(id)
        return buffer ? buffer.duration * 1000 : null
    }

    // ============================
    // Playback
    // ============================

    /**
     * Play a clip from the given offset. Stops any currently playing audio first.
     * Playback is instant because the clip is already an in-memory AudioBuffer.
     *
     * If the clip hasn't been preloaded, this is a no-op (no error thrown).
     *
     * @param {string} clipId
     * @param {number} [offsetMs=0] offset into the clip in milliseconds
     */
    play(clipId, offsetMs = 0) {
        this._stopSource()

        const buffer = this.buffers.get(clipId)
        if (!buffer) {
            this.currentClipId = null
            return
        }

        this._ensureContext()
        if (this.audioContext.state === 'suspended') {
            this.audioContext.resume()
        }

        const source = this.audioContext.createBufferSource()
        source.buffer = buffer
        source.connect(this.gainNode)

        const offsetSec = Math.max(0, Math.min(offsetMs / 1000, buffer.duration))
        source.start(0, offsetSec)

        this.currentSource = source
        this.currentClipId = clipId

        source.onended = () => {
            if (this.currentSource === source) {
                this.currentSource = null
                this.currentClipId = null
            }
        }
    }

    /**
     * Sync audio to the timeline during continuous playback.
     *
     * - If clipId is null/undefined → stops playback.
     * - If clipId matches the currently playing clip → no-op (avoids restarting).
     * - If clipId is different → stops current clip and starts the new one.
     *
     * This is the method to call on every playback tick / playheadTime change.
     *
     * @param {string|null} clipId
     * @param {number} [offsetMs=0]
     */
    syncPlayback(clipId, offsetMs = 0) {
        if (!clipId) {
            if (this.currentClipId) this.stop()
            return
        }
        if (this.currentClipId === clipId) return

        // Don't restart a clip if the offset is past the buffer's end
        // (e.g. the audio naturally ended and the timeline slot is slightly longer)
        const buffer = this.buffers.get(clipId)
        if (buffer && offsetMs / 1000 >= buffer.duration) {
            if (this.currentClipId) this._stopSource()
            this.currentClipId = null
            return
        }

        this.play(clipId, offsetMs)
    }

    /**
     * Stop the currently playing audio.
     */
    stop() {
        this._stopSource()
        this.currentClipId = null
    }

    /**
     * @private
     */
    _stopSource() {
        if (this.currentSource) {
            try { this.currentSource.stop() } catch (_) { /* already stopped */ }
            this.currentSource.disconnect()
            this.currentSource = null
        }
    }

    // ============================
    // Volume
    // ============================

    /**
     * Set master playback volume.
     * @param {number} volume 0.0 (silent) to 1.0 (full)
     */
    setVolume(volume) {
        if (this.gainNode) {
            this.gainNode.gain.value = Math.max(0, Math.min(1, volume))
        }
    }

    // ============================
    // Cleanup
    // ============================

    /**
     * Remove cached buffers for clips that are no longer on the timeline.
     * @param {Set<string>} activeIds – clip IDs to keep
     */
    purge(activeIds) {
        for (const id of this.buffers.keys()) {
            if (!activeIds.has(id)) {
                this.buffers.delete(id)
                this.bufferUrls.delete(id)
            }
        }
    }

    /**
     * Destroy the engine: stop playback, release all buffers, close the AudioContext.
     */
    destroy() {
        this.stop()
        this.buffers.clear()
        this.bufferUrls.clear()
        this.loading.clear()
        if (this.audioContext) {
            this.audioContext.close().catch(() => {})
            this.audioContext = null
            this.gainNode = null
        }
    }
}
